import React from 'react';
import {Stack} from '@chakra-ui/react';
import {UiBox, UiInput, Row, UiButton, UiImage, UiFormControl, UiFormErrorMessage, Col} from '~/components/elements';
import CustomRadio from '~/components/elements/CustomRadio';
import {
  DATA_CHECK_BOX,
  NO_OPTION,
  PH_CHECKOUT_METHOD_KEY,
  POP_UP_PH_CHECKOUT_KEYS,
  QUESTIONS,
  QUESTIONS_PARAMS,
  YES_OPTION,
} from '~/constants/popup';
import {useInput, useRequestState} from '~/hooks';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '~/redux/root-reducer';
import {addQuestionnaire} from '~/redux/questionnaires/thunk';
import {BackAndNextSlide, PopupLayout, TitlePopup} from '~/pages/homes/pop-up/components';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {requestOK} from '~/utils/thunk-api';
import {unwrapResult} from '@reduxjs/toolkit';
import {AppDispatch} from '~/redux/root-store';
import {METHOD_KEYS} from '~/constants/medical-questionnaires';
import {omit} from 'lodash';

export const PopupFour = () => {
  const dispatch: AppDispatch = useDispatch();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;
  const answer = useSelector((state: RootState) => state.questionnaires.collectData);
  const {loading} = useRequestState(addQuestionnaire);

  const questionnaire = answer?.[POP_UP_PH_CHECKOUT_KEYS.MEDICAL_HISTORY];
  const questionnaireInfo = answer?.[POP_UP_PH_CHECKOUT_KEYS.MORE_INFORMATION];
  const {data: answerData} = questionnaire || {};
  const {data: answerDataInfo} = questionnaireInfo || {};

  const defaultValue = {
    selectedOption: answerData?.selectedOption || '',
    tellUsMore: answerData?.tellUsMore || '',
    anyDiseaseHistoryOptions: answerDataInfo?.anyDiseaseHistoryOptions || answerData?.anyDiseaseHistoryOptions || '',
  };

  const rules = {
    tellUsMore: function () {
      if (inputs.selectedOption === YES_OPTION) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {maximum: 300, message: `^This field is too long (maximum is 300 characters)`},
        };
      } else {
        return {presence: false};
      }
    },
  };

  const {inputs, setInputs, validation} = useInput(defaultValue, {rules});

  const isNextSlide =
    inputs.selectedOption === YES_OPTION
      ? Object.values(inputs).every(el => !!el)
      : Object.values(omit(inputs, ['tellUsMore'])).every(el => !!el);

  const handleClickOptions = (value: string, question: string) => {
    setInputs({[question]: value});
  };

  const onClickSubmit = async () => {
    if (validation.getErrors()) return;
    const data =
      inputs.selectedOption === YES_OPTION
        ? inputs
        : {selectedOption: NO_OPTION, anyDiseaseHistoryOptions: inputs.anyDiseaseHistoryOptions};

    const questionnaireAnswer = {
      key: POP_UP_PH_CHECKOUT_KEYS.MEDICAL_HISTORY,
      index: 'SCREEN 4',
      data,
      questions: QUESTIONS_PARAMS.MEDICAL_HISTORY,
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));
    const resAction = await dispatch(
      addQuestionnaire({
        data: {
          data: {
            ...answer,
            [POP_UP_PH_CHECKOUT_KEYS.MEDICAL_HISTORY]: {...questionnaireAnswer},
          },
          wpUserId: wpAuthUser?.wpUserId,
          methodKey: PH_CHECKOUT_METHOD_KEY,
        },
      }),
    );
    if (requestOK(resAction, addQuestionnaire)) {
      const result = unwrapResult(resAction);
      if (window.parent) {
        window.parent.postMessage(
          {
            type: METHOD_KEYS.PH_CHECKOUT,
            data: result,
          },
          '*',
        );
      }
    }
  };

  return (
    <>
      <BackAndNextSlide lastSlide isActiveNextSlide={isNextSlide} />
      <PopupLayout>
        <TitlePopup>{QUESTIONS.MORE_INFORMATION.anyDiseaseHistoryOptions}</TitlePopup>
        <Stack direction="row" my={4}>
          {DATA_CHECK_BOX.map(val => (
            <CustomRadio
              style={{marginRight: 40, cursor: 'pointer'}}
              key={val.label}
              text={val.label}
              checked={inputs.anyDiseaseHistoryOptions === val.label}
              onClick={() => handleClickOptions(val.label, 'anyDiseaseHistoryOptions')}
            />
          ))}
        </Stack>
        <TitlePopup>{QUESTIONS.MEDICAL_HISTORY.selectedOption}</TitlePopup>
        <Stack direction="row" my={4}>
          {DATA_CHECK_BOX.map(val => (
            <CustomRadio
              style={{marginRight: 40, cursor: 'pointer'}}
              key={val.label}
              text={val.label}
              checked={inputs.selectedOption === val.label}
              onClick={() => handleClickOptions(val.label, 'selectedOption')}
            />
          ))}
        </Stack>

        {inputs.selectedOption === YES_OPTION && (
          <UiFormControl id="tellUsMore" isRequired isInvalid={!!validation?.errors?.tellUsMore}>
            <UiBox>
              <TitlePopup>{QUESTIONS.MEDICAL_HISTORY.tellUsMore}</TitlePopup>
              <UiBox>
                <UiInput
                  value={inputs.tellUsMore}
                  onChange={({target}) => setInputs({tellUsMore: target.value})}
                  height="48px"
                  borderRadius="8px"
                />
              </UiBox>
              {validation?.errors?.tellUsMore && (
                <UiFormErrorMessage px={1}>{validation?.errors?.tellUsMore[0]}</UiFormErrorMessage>
              )}
            </UiBox>
          </UiFormControl>
        )}
        <Row justifyContent="flex-end" w="100%" mt={4}>
          <UiButton
            bg="#456762"
            style={{display: 'flex', alignItems: 'center', borderRadius: '5px'}}
            rightIcon={<UiImage type="icon" name="chevron-left" width={5} height={5} />}
            onClick={onClickSubmit}
            disabled={!isNextSlide || loading}
            variant="solid"
            isLoading={loading}
            loadingText="Go to Checkout"
          >
            Go to Checkout
          </UiButton>
        </Row>
      </PopupLayout>
    </>
  );
};
